/**
 * Multiplexed WebSocket wire protocol
 *
 * Industry-standard subscribe/unsubscribe pattern (Slack, TradingView, Coinbase, Discord).
 * Single /ws endpoint replaces the 4 separate connections.
 *
 * Topic naming convention:
 *   ticks:<internalSymbolId>  — symbol-scoped price ticks (any authed user)
 *   signals                   — global new-signal broadcast (any authed user)
 *   positions                 — user's own open positions (auth-resolved server-side)
 *   trades                    — user's own trade events (auth-resolved server-side)
 */

import { z } from 'zod';

// ---------------------------------------------------------------------------
// Client → Server message schemas
// ---------------------------------------------------------------------------

export const clientAuthMessageSchema = z.object({
  type: z.literal('auth'),
  token: z.string().min(1),
});

export const clientSubscribeMessageSchema = z.object({
  type: z.literal('subscribe'),
  topics: z.array(z.string().min(1)).min(1),
});

export const clientUnsubscribeMessageSchema = z.object({
  type: z.literal('unsubscribe'),
  topics: z.array(z.string().min(1)).min(1),
});

export const clientPingMessageSchema = z.object({
  type: z.literal('ping'),
  ts: z.number(),
});

export const clientMessageSchema = z.discriminatedUnion('type', [
  clientAuthMessageSchema,
  clientSubscribeMessageSchema,
  clientUnsubscribeMessageSchema,
  clientPingMessageSchema,
]);

export type ClientMessage = z.infer<typeof clientMessageSchema>;

// ---------------------------------------------------------------------------
// Server → Client message schemas
// ---------------------------------------------------------------------------

export const serverReadyMessageSchema = z.object({
  type: z.literal('ready'),
  userId: z.string(),
});

export const serverAuthErrorMessageSchema = z.object({
  type: z.literal('auth-error'),
  reason: z.string(),
});

export const serverSubscribedMessageSchema = z.object({
  type: z.literal('subscribed'),
  topics: z.array(z.string()),
});

export const serverEventMessageSchema = z.object({
  type: z.literal('event'),
  topic: z.string(),
  data: z.unknown(),
});

export const serverPongMessageSchema = z.object({
  type: z.literal('pong'),
  ts: z.number(),
});

export const serverErrorMessageSchema = z.object({
  type: z.literal('error'),
  message: z.string(),
});

export const serverMessageSchema = z.discriminatedUnion('type', [
  serverReadyMessageSchema,
  serverAuthErrorMessageSchema,
  serverSubscribedMessageSchema,
  serverEventMessageSchema,
  serverPongMessageSchema,
  serverErrorMessageSchema,
]);

export type ServerMessage = z.infer<typeof serverMessageSchema>;

// ---------------------------------------------------------------------------
// Topic helpers
// ---------------------------------------------------------------------------

/** Topic names that require no user-specific resolution */
export const TOPIC_SIGNALS = 'signals' as const;
export const TOPIC_POSITIONS = 'positions' as const;
export const TOPIC_TRADES = 'trades' as const;
export const TOPIC_ANALYSIS = 'analysis' as const;

/** Build a ticks topic for a given symbol id */
export function buildTicksTopic(symbolId: string): string {
  return `ticks:${symbolId}`;
}

/** True if topic is a ticks topic */
export function isTicksTopic(topic: string): boolean {
  return topic.startsWith('ticks:');
}

/** Extract symbolId from a ticks topic. Returns null if invalid. */
export function parseTicksTopic(topic: string): string | null {
  if (!isTicksTopic(topic)) return null;
  const id = topic.slice('ticks:'.length);
  return id.length > 0 ? id : null;
}

/** Resolve a public topic name to the underlying Redis channel */
export function resolveRedisChannel(topic: string, userId: string): string | null {
  if (topic === TOPIC_SIGNALS) return 'signals:approved';
  if (topic === TOPIC_POSITIONS) return `journal:positions:${userId}`;
  if (topic === TOPIC_TRADES) return `trade-events:${userId}`;
  if (topic === TOPIC_ANALYSIS) return `analysis:${userId}`;
  if (isTicksTopic(topic)) {
    const symbolId = parseTicksTopic(topic);
    return symbolId ? `ticks:${symbolId}` : null;
  }
  return null;
}

/**
 * All valid topic names from the public protocol perspective.
 * Used for ACL checks on the server.
 */
export const KNOWN_TOPICS = [TOPIC_SIGNALS, TOPIC_POSITIONS, TOPIC_TRADES, TOPIC_ANALYSIS] as const;
export type KnownTopic = typeof KNOWN_TOPICS[number];

/** Returns true if the topic string is valid per the wire protocol ACL */
export function isValidTopic(topic: string): boolean {
  if ((KNOWN_TOPICS as readonly string[]).includes(topic)) return true;
  if (isTicksTopic(topic)) {
    return parseTicksTopic(topic) !== null;
  }
  return false;
}
